import { useMemo } from 'react'
import { Badge, Text, Group } from '@mantine/core'
import type { BetterUptimeCall } from '@/api/main/types.gen'
import { GenericTable } from './Table/GenericTable'
import { useTable } from './Table/useTable'
import { useHotlineCalls } from '@/hooks/useHotlineCalls'
import { formatFileSize } from '@/utils/other'
import TableLink from './Table/TableLink'
import { formatDateTime } from '@/utils/datetime'
import { useTranslation } from 'react-i18next'

export function HotlineCallsTable({
  startDate,
  endDate,
  hideConfig,
  tableKey,
}: {
  startDate: Date
  endDate: Date
  hideConfig?: boolean
  tableKey: string
}) {
  const { t } = useTranslation('hotline')
  const { data: calls, isLoading, error } = useHotlineCalls(startDate, endDate)

  const enhancedCalls = useMemo(() => calls || [], [calls])

  const table = useTable<BetterUptimeCall>({
    data: enhancedCalls,
    tableKey,
    defaultSortField: 'started_at',
    defaultSortDirection: 'desc',
    itemsPerPage: 20,
    searchDebounceMs: 0,
    columns: [
      {
        key: 'incident_id',
        label: t('id'),
        visible: true,
        sortable: true,
        render: (call: BetterUptimeCall) => <TableLink href={call.incident_url || `#`}>{call.incident_id}</TableLink>,
      },
      {
        key: 'phone_number',
        label: t('phoneNumber'),
        visible: true,
        sortable: true,
      },
      {
        key: 'caller_name',
        label: t('callerName'),
        visible: true,
        sortable: true,
        render: (call: BetterUptimeCall) =>
          call.caller_name === 'N/A' ? (
            <Badge variant="light" color="gray">
              {call.caller_name}
            </Badge>
          ) : (
            <Text>{call.caller_name}</Text>
          ),
      },
      {
        key: 'started_at',
        label: t('started'),
        visible: true,
        sortable: true,
        type: 'date',
        render: (call: BetterUptimeCall) => <Text>{formatDateTime(call.started_at)}</Text>,
      },
      {
        key: 'acknowledged_at',
        label: t('acknowledgedAt'),
        visible: true,
        sortable: true,
        type: 'date',
        render: (call: BetterUptimeCall) =>
          call.acknowledged_at === 'not acknowledged' ? (
            <Badge variant="light" color="orange">
              {t('notAcknowledged', { ns: 'common' })}
            </Badge>
          ) : (
            <Text>{formatDateTime(call.acknowledged_at)}</Text>
          ),
      },
      {
        key: 'acknowledged_by',
        label: t('acknowledgedBy'),
        visible: true,
        sortable: true,
        render: (call: BetterUptimeCall) =>
          call.acknowledged_by === 'not acknowledged' ? (
            <Badge variant="light" color="orange">
              {t('notAcknowledged', { ns: 'common' })}
            </Badge>
          ) : (
            <Text>{call.acknowledged_by}</Text>
          ),
      },
      {
        key: 'attachments',
        label: t('recording'),
        visible: true,
        sortable: false,
        render: (call: BetterUptimeCall) =>
          call.attachments && Object.keys(call.attachments).length > 0 ? (
            Object.entries(call.attachments).map(([key, attachment]) => (
              <Group key={key} gap="xs">
                {attachment.mime_type.startsWith('audio/') ? (
                  <>
                    <Text
                      component="a"
                      href={attachment.url}
                      style={{
                        color: 'var(--mantine-color-blue-6)',
                        textDecoration: 'none',
                      }}
                    >
                      {t('recording')}
                    </Text>
                    <Text size="sm" c="dimmed">
                      ({formatFileSize(attachment.size)})
                    </Text>
                  </>
                ) : null}
              </Group>
            ))
          ) : (
            <Text c="dimmed">{t('noRecording', { ns: 'common' })}</Text>
          ),
      },
    ],
  })

  return (
    <GenericTable
      {...table.tableProps}
      isLoading={isLoading}
      error={error}
      hideConfig={hideConfig}
      configTitle={t('configureHotlineTableView')}
      getRowKey={call => call.incident_id}
      emptyMessage={t('noHotlineCallsFound')}
      hideCsvDownload={false}
      csvFileName="hotline-calls.csv"
    />
  )
}
